import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useInspector, useSaveInspector } from '../../api/inspectors';
import { isApiError } from '../../api/errors';
import { BackLink, Button, ButtonGroup, ButtonLink, ErrorSummary, RequiredFieldsNote } from '../../components/idsk';
import { applyServerErrors, summaryItems } from '../../components/common/FormErrors';
import { VersionConflictSummary } from '../../components/common/VersionConflict';
import { useNavigateWithToast } from '../../components/common/navigateWithToast';
import { useAppForm } from '../../components/common/useAppForm';
import { PageHeading } from '../../layout/PageHeading';
import { usePageTitle } from '../../layout/usePageTitle';
import { inspectorSchema, type InspectorFormValues } from '../../validation/schemas';
import { normalizeRodneCislo, toE164 } from '../../validation/rules';
import { QueryError } from '../common/QueryError';
import { LoadingPage } from '../common/LoadingPage';
import { RequireAdmin } from '../common/RequireAdmin';
import { EMPTY_INSPECTOR, INSPECTOR_SERVER_FIELDS, InspectorFields, inspectorFieldOrder, inspectorToValues } from './InspectorFields';

export function toInspectorRequest(values: InspectorFormValues, version?: number) {
  return {
    ...values,
    personalNumber: normalizeRodneCislo(values.personalNumber),
    phone: toE164(values.phone),
    email: values.email.toLowerCase(),
    version,
  };
}

export function InspectorFormPage() {
  return (
    <RequireAdmin>
      <InspectorForm />
    </RequireAdmin>
  );
}

function InspectorForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const { t } = useTranslation();
  const existing = useInspector(id);
  const save = useSaveInspector(id);
  const navigateWithToast = useNavigateWithToast();
  const [conflict, setConflict] = useState(false);
  const [otherErrors, setOtherErrors] = useState<string[]>([]);
  const title = editing ? t('inspectors.editTitle') : t('inspectors.newTitle');
  usePageTitle([title, t('inspectors.title')]);
  const { register, handleSubmit, formState, setError, reset } = useAppForm<InspectorFormValues>(inspectorSchema, EMPTY_INSPECTOR);

  useEffect(() => {
    if (existing.data) reset(inspectorToValues(existing.data));
  }, [existing.data, reset]);

  if (editing && existing.error) return <QueryError error={existing.error} asPage />;
  if (editing && existing.isPending) return <LoadingPage />;

  const onSubmit = handleSubmit(async (values) => {
    setConflict(false);
    setOtherErrors([]);
    try {
      const saved = await save.mutateAsync(toInspectorRequest(values, existing.data?.version));
      navigateWithToast(`/inspektori/${saved.id}`, t('inspectors.saved', { name: saved.displayName }));
    } catch (e) {
      if (isApiError(e) && e.isVersionConflict) return setConflict(true);
      const unmatched = applyServerErrors(e, setError, INSPECTOR_SERVER_FIELDS);
      if (!isApiError(e) || e.fieldErrors.length === 0 || unmatched.length) setOtherErrors([isApiError(e) ? e.message : t('errors.unexpected'), ...unmatched]);
    }
  });

  const reload = async () => {
    const fresh = await existing.refetch();
    if (fresh.data) reset(inspectorToValues(fresh.data));
    setConflict(false);
  };

  const errors = summaryItems(t, formState.errors, inspectorFieldOrder('inspector'));
  return (
    <>
      <BackLink to={editing ? `/inspektori/${id}` : '/inspektori'} />
      <div className="govuk-grid-row">
        <div className="govuk-grid-column-two-thirds">
          {conflict && <VersionConflictSummary onReload={() => void reload()} focusKey={formState.submitCount} />}
          {!conflict && (errors.length > 0 || otherErrors.length > 0) && (
            <ErrorSummary errors={[...errors, ...otherErrors.map((text) => ({ text }))]} focusKey={formState.submitCount} />
          )}
          <PageHeading>{title}</PageHeading>
          <form onSubmit={(e) => void onSubmit(e)} noValidate>
            <RequiredFieldsNote />
            <InspectorFields prefix="inspector" register={register} errors={formState.errors} />
            <ButtonGroup>
              <Button type="submit" preventDoubleClick>
                {t('common.save')}
              </Button>
              <ButtonLink to={editing ? `/inspektori/${id}` : '/inspektori'} variant="secondary">
                {t('common.cancel')}
              </ButtonLink>
            </ButtonGroup>
          </form>
        </div>
      </div>
    </>
  );
}
